import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth';
import { masterEventService } from '../../services/masterEvent';
import * as db from '../../services/db';
import Avatar from '../shared/Avatar';
import { ROLE_OPTIONS } from '../../services/roles';
import './RootAdmin.css';

const ADMIN_EMAIL = 'chrispalm84@gmail.com';

export default function RootAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [memberships, setMemberships] = useState({});
  const [eventMeta, setEventMeta] = useState({});

  const isRootAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase() || user?.role === 'admin';

  const refresh = useCallback(async () => {
    // Fetch users from DynamoDB, fall back to localStorage
    let users;
    try {
      const dbUsers = await db.listUsers();
      if (dbUsers && dbUsers.length > 0) {
        users = dbUsers
          .filter((u) => !u.userId?.startsWith('invite:'))
          .map((u) => ({ ...u, id: u.userId }));
      }
    } catch (err) {
      console.warn('[RootAdmin] DynamoDB listUsers failed:', err);
    }
    if (!users || users.length === 0) {
      users = authService.getAllUsers();
    }

    const events = await masterEventService.getAll(user);
    setAllUsers(users);
    setAllEvents(events);

    // Build membership map: for each event, get all members, then map by user
    const allMembers = {};
    await Promise.all(events.map(async (e) => {
      const members = await masterEventService.getMembers(e.id);
      members.forEach((m) => {
        const entry = { eventId: e.id, eventName: e.name, userId: m.userId, role: m.role, status: m.status };
        const ids = [m.userId, m.user?.id, m.user?.userId, m.user?.cognitoId].filter(Boolean);
        ids.forEach((uid) => {
          if (!allMembers[uid]) allMembers[uid] = [];
          if (!allMembers[uid].some((mem) => mem.eventId === e.id && mem.userId === m.userId)) {
            allMembers[uid].push(entry);
          }
        });
      });
    }));
    setMemberships(allMembers);

    const meta = {};
    await Promise.all(events.map(async (e) => {
      const [members, activities] = await Promise.all([
        masterEventService.getMembers(e.id),
        masterEventService.getActivities(e.id),
      ]);
      meta[e.id] = { members, activities };
    }));
    setEventMeta(meta);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!isRootAdmin) {
    return (
      <div className="radmin">
        <div className="radmin-denied">
          <h2>Access Denied</h2>
          <p>Root admin access only.</p>
          <button className="radmin-btn" onClick={() => navigate('/')}>Back</button>
        </div>
      </div>
    );
  }

  const handleToggleEnabled = (userId) => {
    const u = allUsers.find((x) => x.id === userId);
    if (u) authService.setEnabled(userId, !u.enabled);
    refresh();
  };

  const handleRemoveFromEvent = async (eventId, userId) => {
    if (!window.confirm('Remove this user from the event?')) return;
    try {
      await masterEventService.removeMember(eventId, userId);
      await refresh();
    } catch (e) { alert(e.message); }
  };

  const handleDeleteUser = (userId) => {
    const u = allUsers.find((x) => x.id === userId);
    if (!window.confirm(`Permanently delete ${u?.displayName || u?.email}? This removes all their data.`)) return;
    try { authService.removeUser(userId); } catch (e) { alert(e.message); }
    refresh();
  };

  const handleChangeRole = async (eventId, userId, newRole) => {
    try {
      await masterEventService.updateMemberRole(eventId, userId, newRole);
      await refresh();
    } catch (e) { alert(e.message); }
  };

  const handleAddToEvent = async (userId, eventId) => {
    const u = allUsers.find((x) => x.id === userId);
    if (!u) return;
    try {
      await masterEventService.inviteByEmail(eventId, u.email, u.displayName, 'participant');
      await refresh();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="radmin">
      <div className="radmin-header">
        <button className="radmin-back" onClick={() => navigate('/')}>← Back</button>
        <h1 className="radmin-title">ROOT ADMIN</h1>
        <p className="radmin-sub">Platform management</p>
      </div>

      {/* Stats */}
      <div className="radmin-stats">
        <div className="radmin-stat">
          <span className="radmin-stat-val">{allUsers.length}</span>
          <span className="radmin-stat-label">USERS</span>
        </div>
        <div className="radmin-stat">
          <span className="radmin-stat-val">{allEvents.length}</span>
          <span className="radmin-stat-label">EVENTS</span>
        </div>
        <div className="radmin-stat">
          <span className="radmin-stat-val">{allUsers.filter((u) => u.cognitoId).length}</span>
          <span className="radmin-stat-label">VERIFIED</span>
        </div>
        <div className="radmin-stat">
          <span className="radmin-stat-val">{allUsers.filter((u) => u.enabled === false).length}</span>
          <span className="radmin-stat-label">DISABLED</span>
        </div>
      </div>

      {/* User Table */}
      <div className="radmin-section">
        <h2 className="radmin-section-title">ALL USERS</h2>
        <div className="radmin-table">
          <div className="radmin-table-header">
            <span style={{ width: 40 }}></span>
            <span style={{ flex: 2 }}>User</span>
            <span style={{ flex: 2 }}>Email</span>
            <span style={{ flex: 1 }}>Status</span>
            <span style={{ flex: 3 }}>Events & Roles</span>
            <span style={{ width: 120 }}>Actions</span>
          </div>
          {allUsers.map((u) => {
            const userMems = [
              ...(memberships[u.id] || []),
              ...(u.cognitoId ? memberships[u.cognitoId] || [] : []),
            ].filter((mem, idx, arr) =>
              arr.findIndex((m) => m.eventId === mem.eventId && m.userId === mem.userId) === idx
            );
            const isPending = !u.cognitoId;
            const isDisabled = u.enabled === false;
            return (
              <div key={u.id} className={`radmin-table-row ${isDisabled ? 'radmin-table-row--disabled' : ''}`}>
                <Avatar user={u} size={22} style={{ width: 40 }} />
                <div style={{ flex: 2 }}>
                  <div className="radmin-user-name">{u.displayName || u.username}</div>
                  <div className="radmin-user-role">{u.role === 'admin' ? 'Root admin' : 'User'}</div>
                </div>
                <div style={{ flex: 2 }}>
                  <div className="radmin-user-email">{u.email}</div>
                  {isPending && <span className="radmin-badge radmin-badge--pending">PENDING</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <button
                    className={`radmin-status-btn ${isDisabled ? 'radmin-status-btn--off' : 'radmin-status-btn--on'}`}
                    onClick={() => handleToggleEnabled(u.id)}
                    disabled={u.role === 'admin'}
                  >
                    {isDisabled ? 'DISABLED' : 'ACTIVE'}
                  </button>
                </div>
                <div style={{ flex: 3 }}>
                  <div className="radmin-event-tags">
                    {userMems.map((mem) => (
                      <div key={`${mem.eventId}:${mem.userId}`} className="radmin-event-tag">
                        <span className="radmin-event-tag-name">{mem.eventName}</span>
                        <select
                          className="radmin-role-select"
                          value={mem.role}
                          onChange={(e) => handleChangeRole(mem.eventId, mem.userId, e.target.value)}
                        >
                          {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        <button
                          className="radmin-event-tag-remove"
                          onClick={() => handleRemoveFromEvent(mem.eventId, mem.userId)}
                          title="Remove from event"
                        >×</button>
                      </div>
                    ))}
                    {(() => {
                      const memberEventIds = new Set(userMems.map((m) => m.eventId));
                      const available = allEvents.filter((e) => !memberEventIds.has(e.id));
                      if (available.length === 0) return null;
                      return (
                        <select
                          className="radmin-role-select"
                          value=""
                          onChange={(e) => { if (e.target.value) handleAddToEvent(u.id, e.target.value); }}
                          title="Add to event"
                          style={{ minWidth: 32 }}
                        >
                          <option value="">+</option>
                          {available.map((ev) => (
                            <option key={ev.id} value={ev.id}>{ev.name}</option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>
                  {userMems.length === 0 && !allEvents.length && (
                    <span className="radmin-no-events">No events</span>
                  )}
                </div>
                <div style={{ width: 120, display: 'flex', gap: 4 }}>
                  {u.role !== 'admin' && (
                    <button className="radmin-action-btn radmin-action-btn--danger" onClick={() => handleDeleteUser(u.id)} title="Delete user">
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Events overview */}
      <div className="radmin-section">
        <h2 className="radmin-section-title">ALL EVENTS</h2>
        <div className="radmin-events-grid">
          {allEvents.map((event) => {
            const members = eventMeta[event.id]?.members || [];
            const activities = eventMeta[event.id]?.activities || [];
            return (
              <div key={event.id} className="radmin-event-card">
                <div className="radmin-event-card-header">
                  <h3>{event.name}</h3>
                  <span className={`radmin-badge radmin-badge--${event.status}`}>{event.status}</span>
                </div>
                <div className="radmin-event-card-stats">
                  <span>{members.length} members</span>
                  <span>{activities.length} activities</span>
                </div>
                <div className="radmin-event-card-members">
                  {members.slice(0, 8).map((m) => (
                    <Avatar key={m.userId} user={m.user} size={18} className="radmin-event-card-avatar" style={{ display: 'inline-block' }} />
                  ))}
                  {members.length > 8 && <span className="radmin-event-card-more">+{members.length - 8}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
